import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  User, 
  ArrowRight, 
  Search,
  Tag,
  Clock,
  TrendingUp
} from 'lucide-react';
import { Banner } from './Banner';

import { blogArticles } from '../data/blogArticles';
import { BlogListSEO } from './SEO';

export const Blog = () => {
  const FALLBACK_IMG = 'data:image/svg+xml;utf8,' + encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"1200\" height=\"630\">\n  <defs>\n    <linearGradient id=\"g\" x1=\"0\" y1=\"0\" x2=\"1\" y2=\"1\">\n      <stop stop-color=\"#1f2937\" offset=\"0\"/>\n      <stop stop-color=\"#111827\" offset=\"1\"/>\n    </linearGradient>\n  </defs>\n  <rect width=\"100%\" height=\"100\%\" fill=\"url(#g)\"/>\n  <text x=\"50%\" y=\"50%\" dominant-baseline=\"middle\" text-anchor=\"middle\" font-family=\"Arial, Helvetica, sans-serif\" font-size=\"40\" fill=\"#e8b86d\">GYMRATREHAB BLOG</text>\n</svg>`);

  const onImgError = (e) => {
    if (e && e.target) {
      e.target.onerror = null;
      e.target.src = FALLBACK_IMG;
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const blogPosts = Array.isArray(blogArticles) ? blogArticles : [];

  const categories = ['all', 'Sports Injuries', 'Orthopedic', 'Movement', 'Recovery'];

  const filteredPosts = (blogPosts || []).filter(post => {
    const title = (post.title || '').toLowerCase();
    const excerpt = (post.excerpt || '').toLowerCase();
    const tagsArr = Array.isArray(post.tags) ? post.tags : [];
    const matchesSearch = title.includes(searchTerm.toLowerCase()) ||
                          excerpt.includes(searchTerm.toLowerCase()) ||
                          tagsArr.some(tag => (tag || '').toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const posts = (blogPosts || []).filter(p => p.slug !== 'movement-assessment');
  const featuredPost = posts.length > 0 ? posts[0] : null;

  const isPiggy = (url) => typeof url === 'string' && url.includes('Saving%20money%20on%20healthcare.jpg');
  const regularPosts = posts.length > 1 ? posts.slice(1) : [];

  return (
    <>
      <BlogListSEO />
      {/* Shared Banner + Pill (reduced) with title fed into Banner */}
      <Banner pillText="Knowledge Hub" title="Physical Therapy Blog" reduced={true} />
      <section id="blog" className="pt-8 pb-20">
      <div className="container">
        {/* Header: removed duplicate title/subtitle to avoid repetition with Banner */}
        <div className="text-center mb-8"></div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-6 mb-12 p-6 bg-gray-900/50 rounded-2xl border border-gray-700">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-black/50 border border-gray-600 rounded-xl text-white focus:outline-none transition-colors"
              style={{ focusBorderColor: 'var(--brand-primary)' }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'text-black'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                style={selectedCategory === category ? { backgroundColor: 'var(--brand-primary)' } : {}}
              >
                {category === 'all' ? 'All Categories' : category}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Post */}
        {searchTerm === '' && selectedCategory === 'all' && featuredPost && (
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp style={{ color: 'var(--brand-primary)' }} size={20} />
              <h2 className="heading-3" style={{ color: 'var(--brand-primary)' }}>Featured Article</h2>
            </div>
            <div className="grid lg:grid-cols-2 gap-8 bg-gray-900/50 rounded-2xl border border-gray-700 overflow-hidden">
              <div className="aspect-video lg:aspect-square">
                {typeof featuredPost.image === 'string' && featuredPost.image.length > 0 ? (
                  <img 
                    src={featuredPost.image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${featuredPost.image}` : featuredPost.image}
                    alt={featuredPost.title || 'Blog image'}
                    onError={onImgError}
                    className={`w-full h-full object-cover ${isPiggy(featuredPost.image) ? 'scale-[0.83]' : ''}`}
                    style={isPiggy(featuredPost.image) ? { imageRendering: 'auto', filter: 'contrast(1.05) saturate(1.03)' } : {}}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-800" />
                )}
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full" style={{ backgroundColor: 'rgba(232, 184, 109, 0.2)', color: 'var(--brand-primary)' }}>
                    {featuredPost.category}
                  </span>
                  <div className="flex items-center text-gray-400 text-sm">
                    <Clock size={14} className="mr-1" />
                    {featuredPost.readTime}
                  </div>
                </div>
                <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-primary)' }}>
                  {featuredPost.title}
                </h3>
                <p className="body-medium mb-6">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-400">
                    <User size={14} className="mr-2" />
                    <span>{featuredPost.author}</span>
                    <span className="mx-2">•</span>
                    <Calendar size={14} className="mr-1" />
                    <span>{featuredPost.date ? new Date(featuredPost.date).toLocaleDateString() : ''}</span>
                  </div>
                  <Link to={`/blog/${featuredPost.slug}`} className="btn-secondary text-sm">
                    Read Article <ArrowRight size={14} className="ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {regularPosts.map((post, idx) => (
            <article key={post.slug || post.title || idx} className="bg-gray-900/50 rounded-2xl border border-gray-700 overflow-hidden transition-colors group hover:border-opacity-75" style={{ '--hover-border-color': 'var(--brand-primary)' }}>
              <div className="aspect-video">
                {typeof post.image === 'string' && post.image.length > 0 ? (
                  <img 
                    src={post.image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${post.image}` : post.image}
                    alt={post.title || 'Blog image'}
                    onError={onImgError}
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${isPiggy(post.image) ? 'scale-[0.83]' : ''}`}
                    style={isPiggy(post.image) ? { imageRendering: 'auto', filter: 'contrast(1.05) saturate(1.03)' } : {}}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-800" />
                )}
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full" style={{ backgroundColor: 'rgba(232, 184, 109, 0.2)', color: 'var(--brand-primary)' }}>
                    {post.category}
                  </span>
                  <div className="flex items-center text-gray-400 text-sm">
                    <Clock size={12} className="mr-1" />
                    {post.readTime}
                  </div>
                </div>
                
                <h3 className="heading-3 mb-3 transition-colors" style={{ color: 'var(--brand-primary)' }}>
                  {post.title}
                </h3>
                
                <p className="body-small mb-4 text-gray-400">
                  {post.excerpt}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {(post.tags || []).slice(0, 3).map(tag => (
                    <span key={tag} className="inline-flex items-center px-2 py-1 bg-gray-800 text-gray-300 text-xs rounded-full">
                      <Tag size={10} className="mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                  <div className="text-xs text-gray-400">
                    <Calendar size={12} className="inline mr-1" />
                    {post.date ? new Date(post.date).toLocaleDateString() : ''}
                  </div>
                  <Link to={`/blog/${post.slug}`} className="font-semibold text-sm transition-colors group" style={{ color: 'var(--brand-primary)' }}>
                    Read More
                    <ArrowRight size={14} className="ml-1 inline group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* No Results */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="text-gray-400" size={32} />
            </div>
            <h3 className="heading-3 mb-4 text-gray-400">No articles found</h3>
            <p className="body-medium text-gray-500 mb-6">
              Try adjusting your search terms or category filter.
            </p>
            <button 
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}
              className="btn-secondary"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Newsletter Signup */}
        <div className="mt-20 p-8 rounded-2xl text-center" style={{ background: 'linear-gradient(to right, rgba(232, 184, 109, 0.1), rgba(232, 184, 109, 0.05))', border: '1px solid rgba(232, 184, 109, 0.2)' }}>
          <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-primary)' }}>Stay Updated</h3>
          <p className="body-medium mb-6 max-w-2xl mx-auto">
            Get the latest articles, treatment tips, and health insights delivered to your inbox. 
            No spam, just valuable content to help you stay healthy and active.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 bg-black/50 border border-gray-600 rounded-xl text-white focus:outline-none transition-colors"
              style={{ ':focus': { borderColor: 'var(--brand-primary)' } }}
            />
            <button className="btn-primary whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};